import { ArchiveIcon } from "@radix-ui/react-icons";
import { Auth0AccountRole } from "client/openapi";
import { DeletionCheck } from "client/openapi/models/DeletionCheck";
import { PersonKind } from "client/openapi/models/PersonKind";
import { PeopleService } from "client/openapi/services/PeopleService";
import { Button, ButtonColor, ButtonFill, ButtonSize } from "components/Button";
import { Dialog, DialogContent } from "components/Dialog";
import { Tooltip, TooltipContent, TooltipTrigger } from "components/Tooltip";
import moment from "moment";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Notifications from "util/notifications";
import { OrgRolesAndAccountContext } from "util/OrgRolesAccountContext";

const ARCHIVE_GRAY = "#606c80";

/** "a", "a and b", "a, b and c". */
function inWords(items: string[]) {
  return items.length < 2
    ? items.join("")
    : `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
}

/**
 * Archive on a profile, or Restore for an archived person, and Delete for
 * someone with no history (so nothing on record is lost). Archive and Delete
 * each ask first. Admins only. A board card's data window passes `size` to
 * match its other buttons, `withDelete={false}` (it offers Archive alone)
 * and `iconOnly`: Archive is an icon with a tooltip, as tall as the row.
 */
export default function ArchivePersonButton({
  kind,
  person,
  onChanged,
  size,
  withDelete = true,
  iconOnly = false,
}: {
  kind: PersonKind;
  person: {
    id: number;
    org_id: number;
    first_name: string;
    last_name: string;
    archived_at?: string | null;
  };
  onChanged: (archived_at: string | null) => void;
  size?: ButtonSize;
  withDelete?: boolean;
  iconOnly?: boolean;
}) {
  const { currently_selected_role } = useContext(OrgRolesAndAccountContext);
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [asking, setAsking] = useState<"archive" | "delete" | null>(null);
  const [deletion, setDeletion] = useState<DeletionCheck>();
  const admin = currently_selected_role === Auth0AccountRole.ORG_ADMIN;
  // Given a size, the buttons take it; otherwise the profile's fixed size.
  const fixed = size ? undefined : "w-20 h-7";

  useEffect(() => {
    if (!admin || !withDelete) return;
    PeopleService.getDeletionCheck({
      orgId: person.org_id,
      kind,
      personId: person.id,
    })
      .then(setDeletion)
      .catch(() => setDeletion(undefined));
  }, [admin, withDelete, kind, person.id, person.org_id]);

  if (!admin) return null;

  const archived = Boolean(person.archived_at);
  const name = `${person.first_name} ${person.last_name}`;
  const args = { orgId: person.org_id, requestBody: { kind, id: person.id } };

  async function save() {
    setSaving(true);
    try {
      const { archived_at } = archived
        ? await PeopleService.restorePerson(args)
        : await PeopleService.archivePerson(args);
      onChanged(archived_at ?? null);
      Notifications.success(`${name} ${archived ? "restored" : "archived"}.`);
    } catch (e: any) {
      Notifications.error(
        e.body?.detail ??
          `Could not ${archived ? "restore" : "archive"}. Try again.`
      );
    }
    setAsking(null);
    setSaving(false);
  }

  async function remove() {
    setSaving(true);
    try {
      await PeopleService.deletePerson(args);
      Notifications.success(`${name} deleted.`);
      // Back to the list they came from, or the Dashboard.
      if ((window.history.state?.idx ?? 0) > 0) navigate(-1);
      else navigate("/dashboard");
    } catch (e: any) {
      // History appeared since the check: say so, and stop offering Delete.
      Notifications.error(e.body?.detail ?? "Could not delete. Try again.");
      if (e.status === 409) setDeletion(undefined);
      setAsking(null);
      setSaving(false);
    }
  }

  const deleteButton = withDelete && deletion?.can_delete && (
    <Button
      color={ButtonColor.RED}
      fill={ButtonFill.HOLLOW}
      size={size}
      disabled={saving}
      onClick={() => setAsking("delete")}
      extraClasses={fixed}
    >
      Delete
    </Button>
  );

  return (
    <div
      className={`flex flex-row flex-wrap gap-2 ${
        iconOnly ? "items-stretch" : "items-center"
      }`}
    >
      {archived ? (
        <>
          <Button
            color={ButtonColor.GREEN}
            size={size}
            disabled={saving}
            onClick={save}
            extraClasses={fixed}
          >
            Restore
          </Button>
          {deleteButton}
          <span className="text-sm text-gray-500">
            Archived {moment(person.archived_at).format("MMM D, YYYY")}
          </span>
        </>
      ) : iconOnly ? (
        <Tooltip>
          <TooltipTrigger>
            <Button
              color={ARCHIVE_GRAY}
              fill={ButtonFill.HOLLOW}
              size={size}
              disabled={saving}
              onClick={() => setAsking("archive")}
              // The row stretches it to its other buttons' height.
              extraClasses="self-stretch"
              aria-label={`Archive ${name}`}
            >
              <ArchiveIcon aria-hidden />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Archive</TooltipContent>
        </Tooltip>
      ) : (
        <>
          <Button
            color={ARCHIVE_GRAY}
            fill={ButtonFill.HOLLOW}
            size={size}
            disabled={saving}
            onClick={() => setAsking("archive")}
            extraClasses={fixed}
          >
            Archive
          </Button>
          {deleteButton}
        </>
      )}

      <Dialog
        open={asking !== null}
        onOpenChange={(open) => !open && !saving && setAsking(null)}
      >
        {asking && (
          <DialogContent className="dialog-content" showClose={false}>
            <div className="p-4">
              <h2 className="text-lg font-bold mb-4 text-center">
                {asking === "archive" ? "Archive" : "Delete"} {name}?
              </h2>
              <p className="mb-4 text-center">
                {asking === "archive"
                  ? "They'll be hidden from lists, pickers and group messages. Their meetings and history stay, and you can restore them anytime."
                  : `Their ${inWords([
                      "profile",
                      ...(deletion?.removes ?? []),
                    ])} will be removed. This can't be undone.`}
              </p>
              <div className="flex justify-center gap-2">
                <Button
                  color={ButtonColor.GRAY}
                  disabled={saving}
                  onClick={() => setAsking(null)}
                >
                  Cancel
                </Button>
                {asking === "archive" ? (
                  <Button color={ARCHIVE_GRAY} disabled={saving} onClick={save}>
                    Archive
                  </Button>
                ) : (
                  <Button
                    color={ButtonColor.RED}
                    disabled={saving}
                    onClick={remove}
                  >
                    Delete
                  </Button>
                )}
              </div>
            </div>
          </DialogContent>
        )}
      </Dialog>
    </div>
  );
}
